import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getApiErrorMessage } from "../lib/apiErrors";
import { connectBroker, getBrokers } from "../services/api.broker";
import { GlassTitle } from "../components/GlassTitle";
import {
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PlatformShell from "../components/PlatformShell";

const FIELD_LABELS = {
  username: "Username",
  api_key: "API Key / Passcode",
  password: "Password",
  account_id: "Account ID",
};

const SECRET_FIELDS = new Set(["api_key", "password"]);

const ACCOUNT_TYPE_LABELS = {
  sim: "Sim",
  live: "Live",
  funded: "Funded",
  evaluation: "Evaluation",
};

const ACCOUNT_TYPE_COLORS = {
  sim: "bg-zinc-700 text-zinc-300",
  live: "bg-green-900/60 text-green-300",
  funded: "bg-blue-900/60 text-blue-300",
  evaluation: "bg-amber-900/60 text-amber-300",
};

// ─── State 0: No subscription ────────────────────────────────────────────────
function NoSubscriptionView({ onViewPlans }) {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center">
        <AlertCircle className="w-8 h-8 text-zinc-400" />
      </div>
      <h2 className="text-2xl font-bold mb-3">Subscription Required</h2>
      <p className="text-zinc-400 text-sm max-w-sm mx-auto mb-8">
        Connect your broker account to start trading. An active subscription is
        required to link a broker.
      </p>
      <Button
        className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        onClick={onViewPlans}
      >
        View Plans →
      </Button>
    </div>
  );
}

// ─── State 1: Broker selector + credential form ───────────────────────────────
function ConnectView({ brokers, onConnected, onSkip }) {
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showSecret, setShowSecret] = useState({});

  const handleBrokerSelect = (broker) => {
    setSelectedBroker(broker);
    setCredentials({});
    setError(null);
  };

  const handleCredentialChange = (field, value) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
  };

  const toggleShowSecret = (field) => {
    setShowSecret((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleConnect = async () => {
    if (!selectedBroker) return;
    setConnecting(true);
    setError(null);
    try {
      const response = await connectBroker({
        broker_name: selectedBroker.name,
        credentials,
      });
      onConnected(response.data.trading_accounts || []);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, "Failed to connect broker. Check your credentials.");
      const isSubscriptionGate =
        err.response?.status === 403 &&
        /active subscription is required|please upgrade your plan|subscription is required/i.test(errorMessage);

      if (isSubscriptionGate) {
        onConnected(null, true);
        return;
      }

      setError(errorMessage);
    } finally {
      setConnecting(false);
    }
  };

  const canSubmit = selectedBroker && !connecting;

  return (
    <div>
      {/* Step header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">
            Step 1 · Connect your broker
          </p>
          <h2 className="text-2xl font-bold">Select your broker</h2>
          <p className="text-zinc-400 text-sm mt-1">
            Enter your API credentials to link your trading account.
          </p>
        </div>
        <Badge className="bg-white/[0.06] text-zinc-400 border border-white/10 text-xs">
          1 of 1
        </Badge>
      </div>

      {/* Broker grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {brokers.map((broker) => (
          <button
            key={broker.id}
            type="button"
            onClick={() => handleBrokerSelect(broker)}
            className={`text-left p-4 rounded-2xl border transition-all duration-150 ${
              selectedBroker?.id === broker.id
                ? "border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/40"
                : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-sm">{broker.display_name}</p>
                {broker.description && (
                  <p className="text-xs text-zinc-500 mt-0.5">{broker.description}</p>
                )}
              </div>
              <Badge className="bg-white/[0.06] text-zinc-400 border border-white/10 text-xs shrink-0">
                {broker.auth_type}
              </Badge>
            </div>
          </button>
        ))}
      </div>

      {/* Credential form */}
      {selectedBroker && selectedBroker.required_fields?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 mb-6 space-y-4">
          <p className="text-sm font-medium text-zinc-300">
            {selectedBroker.display_name} credentials
          </p>
          {selectedBroker.required_fields.map((field) => {
            const isSecret = SECRET_FIELDS.has(field);
            const visible = showSecret[field];
            return (
              <div key={field} className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">
                  {FIELD_LABELS[field] || field}
                </Label>
                <div className="relative">
                  <Input
                    type={isSecret && !visible ? "password" : "text"}
                    value={credentials[field] || ""}
                    onChange={(e) => handleCredentialChange(field, e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white pr-10"
                    placeholder={FIELD_LABELS[field] || field}
                  />
                  {isSecret && (
                    <button
                      type="button"
                      onClick={() => toggleShowSecret(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {visible ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-900/30 border border-red-500/30 text-red-300 text-sm mb-4">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <Button
        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        disabled={!canSubmit}
        onClick={handleConnect}
      >
        {connecting ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : null}
        {connecting ? "Connecting…" : "Connect Broker"}
      </Button>

      <div className="text-center mt-4">
        <button
          type="button"
          onClick={onSkip}
          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Skip for now — go to Dashboard
        </button>
      </div>
    </div>
  );
}

// ─── State 2: Success ─────────────────────────────────────────────────────────
function SuccessView({ accounts, onContinue, onConnectAnother }) {
  return (
    <div>
      {/* Success header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 text-green-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Broker connected!</h2>
          <p className="text-zinc-400 text-sm">
            {accounts.length} account{accounts.length !== 1 ? "s" : ""} synced
          </p>
        </div>
      </div>

      {/* Account cards */}
      {accounts.length > 0 && (
        <div className="space-y-3 mb-8">
          {accounts.map((account) => {
            const typeKey = account.account_type?.toLowerCase() || "sim";
            return (
              <div
                key={account.id}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-semibold text-sm">{account.account_name}</p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
                      ACCOUNT_TYPE_COLORS[typeKey] || ACCOUNT_TYPE_COLORS.sim
                    }`}
                  >
                    {ACCOUNT_TYPE_LABELS[typeKey] || typeKey}
                  </span>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    ${(account.balance || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-zinc-500">balance</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button
        className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-3"
        onClick={onContinue}
      >
        Continue to Dashboard →
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onConnectAnother}
          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Connect another broker
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const OnboardingBrokerConnect = () => {
  const navigate = useNavigate();

  // "none" | "connect" | "success" | "no-subscription"
  const [view, setView] = useState("connect");
  const [brokers, setBrokers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);

  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        const res = await getBrokers();
        setBrokers(res.data || []);
      } catch (err) {
        toast.error("Failed to load broker list");
      } finally {
        setLoadingBrokers(false);
      }
    };
    fetchBrokers();
  }, []);

  const handleConnected = (tradingAccounts, subscriptionRequired = false) => {
    if (subscriptionRequired) {
      setView("no-subscription");
      return;
    }
    setAccounts(tradingAccounts || []);
    setView("success");
  };

  const handleConnectAnother = () => {
    setAccounts([]);
    setView("connect");
  };

  return (
    <PlatformShell title="Connect a Broker" titleIcon={AlertCircle} backTo="/dashboard" currentKey="broker">
      <div className="max-w-2xl mx-auto px-6 py-14">
        <div className="mb-8">
          <GlassTitle as="h1" size="lg" className="text-3xl font-bold mb-1">
            Connect a Broker
          </GlassTitle>
          <p className="text-zinc-400 text-sm">
            Link your trading account to start using TradoPro's automation.
          </p>
        </div>

        <Card className="card-gloss border-0 rounded-2xl">
          <CardContent className="p-8">
            {loadingBrokers && view !== "no-subscription" ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : view === "no-subscription" ? (
              <NoSubscriptionView onViewPlans={() => navigate("/subscription")} />
            ) : view === "success" ? (
              <SuccessView
                accounts={accounts}
                onContinue={() => navigate("/dashboard")}
                onConnectAnother={handleConnectAnother}
              />
            ) : (
              <ConnectView
                brokers={brokers}
                onConnected={handleConnected}
                onSkip={() => navigate("/dashboard")}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </PlatformShell>
  );
};

export default OnboardingBrokerConnect;
